import { ChevronRight, TriangleAlert } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { buildBreakdownColumns, measureValueOf } from './breakdown-columns'
import type {
  ReportDimension,
  ReportMeasure,
  ReportRow,
  ReportTotals,
} from '@/features/reports/api/use-sales-report-query'
import { ShareBar } from '@/features/reports/components/share-bar'
import { hourRowsSpanDays } from '@/features/reports/lib/dimension-labels'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import { cn } from '@/lib/utils'

interface BreakdownTableProps {
  dimension: ReportDimension
  measure: ReportMeasure
  rows: Array<ReportRow>
  total: ReportTotals
  previous: ReportTotals | undefined
  /** Null when this dimension has no level below it — rows are then not clickable. */
  onDrill: ((row: ReportRow) => void) | null
  loading?: boolean
}

/**
 * The classic table the owner asked for (CHG-2026-09-manager-reporting, D19).
 *
 * Hand-rolled rather than `DataTable`, which owns its own query, paging and filters — all three of
 * which this page has to own instead, because one filter row scopes the whole page and the
 * dimension switch changes the columns.
 *
 * Two things the design insists on and a generic table has no notion of: the **total row is pinned
 * above** the rows, in its own `thead` group so it stays visible and reprints on every page; and
 * the share is an inline bar in the row rather than a percentage in a column of its own.
 */
export function BreakdownTable({
  dimension,
  measure,
  rows,
  total,
  previous,
  onDrill,
  loading,
}: BreakdownTableProps) {
  const { t } = useTranslation('reports')
  const columns = buildBreakdownColumns(t, dimension, measure, {
    withDate: hourRowsSpanDays(rows),
  })
  const totalValue = measureValueOf(
    { ...EMPTY_ROW, revenueCents: total.revenueCents, units: total.units },
    measure,
  )

  if (loading) return <Skeleton className="h-72 w-full" />

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border py-16 text-center text-sm text-muted-foreground">
        {t('empty')}
      </div>
    )
  }

  return (
    <div
      className="w-full overflow-x-auto rounded-xl border border-border"
      data-print-fit
    >
      <table className="w-full caption-bottom text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  'h-10 whitespace-nowrap px-3 align-middle font-medium text-muted-foreground',
                  column.align === 'right' ? 'text-right' : 'text-left',
                )}
              >
                {column.label}
              </th>
            ))}
            {onDrill ? <th className="w-8" /> : null}
          </tr>
          {/* Pinned above the rows on purpose: the reader's first question is the total, and a
              footer total is below the fold on any long table. */}
          <tr className="border-b-2 border-border bg-muted/50 font-semibold">
            {columns.map((column) => (
              <td
                key={column.key}
                className={cn(
                  'whitespace-nowrap px-3 py-2.5 align-middle',
                  column.align === 'right'
                    ? 'text-right tabular-nums'
                    : 'text-left',
                  column.kind === 'share' && 'w-52 min-w-40',
                )}
              >
                {/* The 100 % the rows below are a share of, drawn rather than stated: a column of
                    percentages with no full bar at the top leaves the reader guessing what the
                    longest bar means. */}
                {column.kind === 'share' ? (
                  <ShareBar share={1} tone="reference" />
                ) : (
                  renderTotalCell(t, column.key, measure, total, previous)
                )}
              </td>
            ))}
            {onDrill ? <td /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const clickable = onDrill != null
            return (
              <tr
                key={row.key}
                className={cn(
                  'border-b border-border last:border-0',
                  clickable && 'cursor-pointer hover:bg-muted/50',
                  row.isCollector && 'bg-warning/5',
                )}
                onClick={clickable ? () => onDrill(row) : undefined}
                // Keyboard reach for the same drill-down; a row that only responds to a pointer is
                // a dead end for anyone tabbing through the table.
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onDrill(row)
                        }
                      }
                    : undefined
                }
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-3 py-2.5 align-middle',
                      column.align === 'right'
                        ? 'whitespace-nowrap text-right tabular-nums'
                        : 'text-left',
                      column.kind === 'share' && 'w-52 min-w-40',
                      column.kind === 'measure' && 'font-semibold',
                      column.kind === 'delta' && 'text-muted-foreground',
                    )}
                    data-print-tight={column.align === 'right' ? '' : undefined}
                  >
                    {column.kind === 'share' ? (
                      <ShareBar
                        share={
                          totalValue > 0
                            ? measureValueOf(row, measure) / totalValue
                            : 0
                        }
                        tone={row.isCollector ? 'warning' : 'magnitude'}
                      />
                    ) : column.kind === 'label' ? (
                      <span className="flex items-center gap-1.5">
                        {row.isCollector ? (
                          <TriangleAlert
                            className="h-3.5 w-3.5 shrink-0 text-warning"
                            aria-hidden="true"
                          />
                        ) : null}
                        <span className="truncate">{column.format(row)}</span>
                        {/* A subgroup that is really a main group says so, so the column still
                            sums and nobody wonders why a parent is missing. */}
                        {dimension === 'category_sub' &&
                        row.parentKey == null ? (
                          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            {t('groups.isMainGroup')}
                          </span>
                        ) : null}
                        {row.parentLabel ? (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            · {row.parentLabel}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      column.format(row)
                    )}
                  </td>
                ))}
                {onDrill ? (
                  <td className="pr-2 text-right">
                    <ChevronRight
                      className="inline h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const EMPTY_ROW: ReportRow = {
  key: '',
  label: null,
  parentKey: null,
  parentLabel: null,
  revenueCents: 0,
  units: 0,
  bons: 0,
  skus: 0,
  previousRevenueCents: null,
  previousUnits: null,
  isCollector: false,
}

function renderTotalCell(
  t: (key: string, options?: Record<string, unknown>) => string,
  columnKey: string,
  measure: ReportMeasure,
  total: ReportTotals,
  previous: ReportTotals | undefined,
): string {
  switch (columnKey) {
    case 'label':
      return t('columns.total')
    case 'revenue':
      return formatCurrency(total.revenueCents)
    case 'units':
      return formatNumber(total.units)
    case 'bons':
      return formatNumber(total.bons)
    case 'averageBon':
      return total.bons > 0
        ? formatCurrency(Math.round(total.revenueCents / total.bons))
        : '—'
    case 'delta': {
      const current = measure === 'units' ? total.units : total.revenueCents
      const before = previous
        ? measure === 'units'
          ? previous.units
          : previous.revenueCents
        : null
      if (before == null || before === 0) return '—'
      const ratio = (current - before) / before
      return new Intl.NumberFormat('de-AT', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        signDisplay: 'exceptZero',
      }).format(ratio)
    }
    default:
      return ''
  }
}
